/**
 * Pre-Confirmation Edit Page
 * Allows users to edit lead data before confirmation via web interface
 */

import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { Form, Input, Button, Alert, Typography, Space, Spin } from "antd";
import { SaveOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import EditLeadForm from "../components/EditLeadForm";
import api from "../shared/api";

const { Text } = Typography;
const { TextArea } = Input;

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 8px;

  @media (min-width: 480px) {
    padding: 16px;
  }

  @media (min-width: 768px) {
    padding: 32px;
  }
`;

const Header = styled.div`
  background: white;
  border-radius: 12px 12px 0 0;
  padding: 16px;

  @media (min-width: 480px) {
    padding: 20px;
  }

  @media (min-width: 768px) {
    padding: 24px;
  }
  border-bottom: 1px solid #f0f0f0;
`;

const FormContainer = styled.div`
  background: white;
  border-radius: 0 0 12px 12px;
  padding: 16px;

  @media (min-width: 480px) {
    padding: 20px;
  }

  @media (min-width: 768px) {
    padding: 24px;
  }
`;

const BackButton = styled(Button)`
  margin-bottom: 16px;
`;

const PreConfirmationEdit = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [leadData, setLeadData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Get WhatsApp contact number from environment
  const whatsappNumber = import.meta.env.VITE_CONTACT_PHONE || "27671579406";
  const whatsappUrl = `https://wa.me/${whatsappNumber}`;

  useEffect(() => {
    fetchLeadData();
  }, [token]);

  const fetchLeadData = async () => {
    try {
      setLoading(true);
      const data = await api.get(`/pre-confirmation-edit/${token}`);

      if (data.success) {
        setLeadData(data.lead);
      } else {
        setError(data.error || "Failed to load lead data");
      }
    } catch (err) {
      console.error("Error fetching lead data:", err);
      setError("Failed to load lead data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (formData) => {
    try {
      setSubmitting(true);
      setError(null);

      const data = await api.put(`/pre-confirmation-edit/${token}`, {
        leadData: formData,
      });

      if (data.success) {
        // Show success message and redirect back to WhatsApp
        alert(
          "✅ Changes saved! You will be redirected to WhatsApp to confirm your request."
        );
        // Redirect to WhatsApp
        window.location.href = whatsappUrl;
      } else {
        setError(data.error || "Failed to save changes");
      }
    } catch (err) {
      console.error("Error saving changes:", err);
      setError("Failed to save changes");
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    // Redirect back to WhatsApp
    window.location.href = whatsappUrl;
  };

  if (loading) {
    return (
      <Container>
        <div style={{ textAlign: "center", padding: "50px" }}>
          <Spin size="large" />
          <div style={{ marginTop: "16px" }}>
            <Text>Loading your moving request...</Text>
          </div>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <div style={{ textAlign: "center", padding: "50px" }}>
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: "16px" }}
          />
          <Button onClick={handleBack} icon={<ArrowLeftOutlined />}>
            Back to WhatsApp
          </Button>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <BackButton onClick={handleBack} icon={<ArrowLeftOutlined />}>
          Back to WhatsApp
        </BackButton>
        <Typography.Title level={2} style={{ margin: 0 }}>
          ✏️ Edit Your Moving Request
        </Typography.Title>
        <Text type="secondary">
          Make your changes below. When you're done, click "Save Changes" and
          return to WhatsApp to confirm your request.
        </Text>
      </Header>

      <FormContainer>
        <EditLeadForm
          initialData={leadData}
          onSubmit={handleSubmit}
          loading={submitting}
          error={error}
        />
      </FormContainer>
    </Container>
  );
};

export default PreConfirmationEdit;
